import { useWeeklyRecap } from "@/hooks/useSocialRetention";
import { BookOpen, Zap, Trophy, Flame, Sparkles } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { AnyValue } from "@/lib/rowTypes";

// Hidden / retired curriculum topics that must never surface in user-facing stats.
const HIDDEN_TOPICS = new Set([
  "Financial Confidence System",
  "FCI",
  "Financial Confidence Index",
]);

export function WeeklyRecapCard() {
  const { recap, loading } = useWeeklyRecap();

  if (loading) return <Skeleton className="h-32 w-full rounded-xl" />;
  if (!recap) return null;

  const totalChallenges = recap.challenge_wins + recap.challenge_losses;
  const winRate = totalChallenges > 0 ? Math.round((recap.challenge_wins / totalChallenges) * 100) : null;

  const visibleTopic =
    recap.top_topic && !HIDDEN_TOPICS.has(recap.top_topic) ? recap.top_topic : null;

  const headline = recap.lessons_completed === 0 && recap.xp_gained === 0
    ? "Quiet week. A 5-min lesson restarts your momentum."
    : recap.challenge_wins > recap.challenge_losses
      ? "You're on a roll — keep the pressure on."
      : recap.xp_gained >= 200
        ? "Strong learning week. Your future self thanks you."
        : "Steady progress beats perfect intentions.";

  return (
    <div className="glass-card p-5 space-y-4 relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between gap-2 relative">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <h3 className="font-heading font-bold text-foreground">This Week</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Last 7 Days</span>
      </div>
      <p className="text-sm text-muted-foreground italic">{headline}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={BookOpen} label="Lessons" value={recap.lessons_completed} tone="text-primary" />
        <Stat icon={Zap} label="XP" value={recap.xp_gained} tone="text-secondary" />
        <Stat icon={Trophy} label="Wins" value={recap.challenge_wins} sub={winRate !== null ? `${winRate}%` : undefined} tone="text-emerald-400" />
        <Stat icon={Flame} label="Streak" value={recap.current_streak} sub="days" tone="text-orange-400" />
      </div>
      <p className="text-xs text-muted-foreground border-t border-border pt-3">
        Strongest topic:{" "}
        {visibleTopic ? (
          <span className="text-foreground font-medium">{visibleTopic}</span>
        ) : (
          <span className="text-muted-foreground/80 italic">Not enough topic data yet</span>
        )}
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, tone }: { icon: AnyValue; label: string; value: number; sub?: string; tone: string }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
      <div>
        <p className="text-xl font-heading font-bold text-foreground leading-none">{value}{sub && <span className="text-xs text-muted-foreground ml-1 font-normal">{sub}</span>}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{label}</p>
      </div>
    </div>
  );
}
